import React, { useState, useEffect } from "react";
import { useAuth } from "../providers/AuthContext";
import circleLogo from "../assets/circleLogo.png";

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isInitializing } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState("");

  // Reset signing in state if user navigates back without completing auth
  useEffect(() => {
    if (!isInitializing && !isAuthenticated && isSigningIn) {
      // If we're not initializing, not authenticated, but still signing in,
      // it means the user likely navigated back from Auth0
      setIsSigningIn(false);
    }
  }, [isAuthenticated, isInitializing, isSigningIn]);

  // Add timeout as fallback to prevent infinite loading
  useEffect(() => {
    if (isSigningIn) {
      const timeout = setTimeout(() => {
        setIsSigningIn(false);
        setError("Sign in timed out. Please try again.");
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isSigningIn]);

  const handleSignIn = async () => {
    setError("");
    setIsSigningIn(true);
    try {
      await login();
    } catch (error) {
      setError("Failed to sign in. Please try again.");
      console.error("Login error:", error);
      setIsSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col justify-between bg-gradient-to-br from-[#e3f2fd] via-[#f5f7fa] to-[#c9e7fa] px-4">
      {/* Centered Content */}
      <div className="bg-transparent flex flex-col items-center justify-center flex-1">
        {/* Logo Area */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center">
          <div className="bg-[#00a8ca] shadow-lg rounded-full p-3 sm:p-4 mb-3">
            <img
              src={circleLogo}
              alt="Aveta Assistant"
              className="h-14 w-14 sm:h-20 sm:w-20 object-contain"
            />
          </div>
          <span className="text-[#46ACC6] font-bold text-lg sm:text-xl tracking-wide drop-shadow-sm">
            Aveta Assistant
          </span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 w-full max-w-xs text-center">
            <div className="text-red-700 text-sm">{error}</div>
          </div>
        )}

        {/* Standalone Sign In Button */}
        <button
          onClick={handleSignIn}
          data-trust-trigger="sign-in-button"
          disabled={isSigningIn}
          className="mt-2 w-full max-w-xs flex items-center justify-center gap-3 bg-[#46ACC6] hover:bg-[#3a8ba3] active:bg-[#2e6d7a] disabled:bg-[#46ACC6] disabled:opacity-70 text-white font-bold py-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#46ACC6] focus:ring-offset-2 shadow-lg text-lg text-center"
        >
          {isSigningIn ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <span>Sign in with Auth0</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;
